import { useCallback, useEffect, useState } from 'react';

import { DEFAULT_PANE_ID_PREFIX } from '../constants';
import type { ChartV2Series, ChartV2SeriesType } from '../types/chart-v2.types';
import {
  compactPaneIndices,
  InternalSeriesMeta,
  seriesPaneId,
} from '../utils/panes';

export interface UseSeriesMetaOptions {
  series: ChartV2Series[];
  onSeriesColorChange?: (
    seriesId: string,
    color: string,
    colorIndex: number
  ) => void;
  onSeriesTypeChange?: (
    seriesId: string,
    seriesType: ChartV2SeriesType
  ) => void;
  /** One-shot notify for the top chart-type chip (all series). Optional. */
  onAllSeriesTypeChange?: (seriesType: ChartV2SeriesType) => void;
  onSeriesMovePane?: (seriesId: string, direction: 'up' | 'down') => void;
  onPaneMove?: (paneId: string, direction: 'up' | 'down') => void;
}

export interface UseSeriesMetaResult {
  seriesMeta: Record<string, InternalSeriesMeta>;
  handleSeriesColorChange: (
    seriesId: string,
    color: string,
    colorIndex: number
  ) => void;
  handlePerSeriesTypeChange: (
    seriesId: string,
    seriesType: ChartV2SeriesType
  ) => void;
  /**
   * Stores a series' type without firing `onSeriesTypeChange`, for callers
   * that notify the host through a different single write.
   */
  setSeriesType: (seriesId: string, seriesType: ChartV2SeriesType) => void;
  handleAllSeriesTypeChange: (seriesType: ChartV2SeriesType) => void;
  handleSeriesMovePane: (seriesId: string, direction: 'up' | 'down') => void;
  handlePaneMove: (
    paneId: string,
    direction: 'up' | 'down',
    visibleSeries: ChartV2Series[]
  ) => void;
}

const parseIndexFromId = (id: string): number => {
  const n = Number(id.replace(DEFAULT_PANE_ID_PREFIX, ''));

  return Number.isFinite(n) ? n : 0;
};

export const useSeriesMeta = ({
  series,
  onSeriesColorChange,
  onSeriesTypeChange,
  onAllSeriesTypeChange,
  onSeriesMovePane,
  onPaneMove,
}: UseSeriesMetaOptions): UseSeriesMetaResult => {
  const [seriesMeta, setSeriesMeta] = useState<
    Record<string, InternalSeriesMeta>
  >({});

  // Reconcile internal meta with the currently-provided series list. Keep
  // existing entries for known ids, initialise new ones, drop stale ones.
  useEffect(() => {
    setSeriesMeta(prev => {
      if (
        series.length === Object.keys(prev).length &&
        series.every(s => prev[s.id])
      ) {
        return prev;
      }

      const next: Record<string, InternalSeriesMeta> = {};

      for (const s of series) {
        next[s.id] = prev[s.id] ?? { paneIndex: 0 };
      }

      return next;
    });
  }, [series]);

  const handleSeriesColorChange = useCallback(
    (seriesId: string, color: string, colorIndex: number) => {
      setSeriesMeta(prev => ({
        ...prev,
        [seriesId]: { ...(prev[seriesId] ?? { paneIndex: 0 }), color },
      }));
      onSeriesColorChange?.(seriesId, color, colorIndex);
    },
    [onSeriesColorChange]
  );

  const setSeriesType = useCallback(
    (seriesId: string, seriesType: ChartV2SeriesType) => {
      setSeriesMeta(prev => ({
        ...prev,
        [seriesId]: { ...(prev[seriesId] ?? { paneIndex: 0 }), seriesType },
      }));
    },
    []
  );

  const handlePerSeriesTypeChange = useCallback(
    (seriesId: string, seriesType: ChartV2SeriesType) => {
      setSeriesType(seriesId, seriesType);
      onSeriesTypeChange?.(seriesId, seriesType);
    },
    [setSeriesType, onSeriesTypeChange]
  );

  const handleAllSeriesTypeChange = useCallback(
    (seriesType: ChartV2SeriesType) => {
      setSeriesMeta(prev => {
        const next: Record<string, InternalSeriesMeta> = { ...prev };

        for (const s of series) {
          next[s.id] = { ...(prev[s.id] ?? { paneIndex: 0 }), seriesType };
        }

        return next;
      });

      onAllSeriesTypeChange?.(seriesType);
    },
    [series, onAllSeriesTypeChange]
  );

  const handleSeriesMovePane = useCallback(
    (seriesId: string, direction: 'up' | 'down') => {
      setSeriesMeta(prev => {
        const current = prev[seriesId];

        if (!current) {
          return prev;
        }

        const next =
          direction === 'up'
            ? Math.max(0, current.paneIndex - 1)
            : current.paneIndex + 1;

        if (next === current.paneIndex) {
          return prev;
        }

        const updated: Record<string, InternalSeriesMeta> = {
          ...prev,
          [seriesId]: { ...current, paneIndex: next },
        };

        return compactPaneIndices(updated);
      });
      onSeriesMovePane?.(seriesId, direction);
    },
    [onSeriesMovePane]
  );

  const handlePaneMove = useCallback(
    (
      paneId: string,
      direction: 'up' | 'down',
      visibleSeries: ChartV2Series[]
    ) => {
      setSeriesMeta(prev => {
        // Build the current pane order from the visible series so the move
        // operates on the same set the user actually sees.
        const paneOrder: string[] = [];
        const paneIdToOrder = new Map<string, number>();

        visibleSeries.forEach(s => {
          const id = seriesPaneId(s);

          if (!paneIdToOrder.has(id)) {
            paneIdToOrder.set(id, paneOrder.length);
            paneOrder.push(id);
          }
        });

        const currentOrderIndex = paneIdToOrder.get(paneId);

        if (currentOrderIndex === undefined) {
          return prev;
        }

        const targetOrderIndex =
          direction === 'up' ? currentOrderIndex - 1 : currentOrderIndex + 1;

        if (targetOrderIndex < 0 || targetOrderIndex >= paneOrder.length) {
          return prev;
        }

        const targetPaneId = paneOrder[targetOrderIndex];
        const fromIdx = parseIndexFromId(paneId);
        const toIdx = parseIndexFromId(targetPaneId);
        const next: Record<string, InternalSeriesMeta> = { ...prev };

        // Swap stored paneIndex values for every series in either pane.
        for (const id of Object.keys(next)) {
          const meta = next[id];

          if (meta.paneIndex === fromIdx) {
            next[id] = { ...meta, paneIndex: toIdx };
          } else if (meta.paneIndex === toIdx) {
            next[id] = { ...meta, paneIndex: fromIdx };
          }
        }

        return compactPaneIndices(next);
      });
      onPaneMove?.(paneId, direction);
    },
    [onPaneMove]
  );

  return {
    seriesMeta,
    handleSeriesColorChange,
    handlePerSeriesTypeChange,
    setSeriesType,
    handleAllSeriesTypeChange,
    handleSeriesMovePane,
    handlePaneMove,
  };
};
